import React, {
  memo,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Avatar,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { OrgFlowActionsContext } from './SAP_BASIC_01_OrgFlow';
import {
  ORG_DEPT_NODE_WIDTH,
  ORG_DEPT_HEADER_MIN_HEIGHT,
  SAP_ORG_EXPORT_HIDE_CLASS,
} from './SAP_BASIC_01_OrgFlowUtils';

const HANDLE_CLASS = 'sap-org-dept-handle';
const HANDLE_SIZE = 8;

const PORTS = [
  { id: 'left', position: Position.Left },
  { id: 'right', position: Position.Right },
  { id: 'top', position: Position.Top },
  { id: 'bottom', position: Position.Bottom },
];

const GetSideHandleStyle = (centerY) => ({
  top: centerY,
  transform: 'translateY(-50%)',
});

const GetHandleStyle = (portId, sideHandleCenterY) => {
  switch (portId) {
    case 'left':
      return {
        ...GetSideHandleStyle(sideHandleCenterY),
        left: 0,
        transform: 'translate(-50%, -50%)',
      };
    case 'right':
      return {
        ...GetSideHandleStyle(sideHandleCenterY),
        right: 0,
        left: 'auto',
        transform: 'translate(50%, -50%)',
      };
    case 'top':
      return {
        top: 0,
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    case 'bottom':
      return {
        top: 'auto',
        bottom: 0,
        left: '50%',
        transform: 'translate(-50%, 50%)',
      };
    default:
      return undefined;
  }
};

function SAP_BASIC_01_DeptNode({ id, data, selected }) {
  const actions = useContext(OrgFlowActionsContext);
  const updateNodeInternals = useUpdateNodeInternals();
  const rootRef = useRef(null);
  const headerRef = useRef(null);
  const [sideHandleCenterY, setSideHandleCenterY] = useState(
    ORG_DEPT_HEADER_MIN_HEIGHT / 2
  );
  const dept = data?.dept ?? {};
  const members = data?.members ?? [];
  const isDragOver = data?.isDragOver === true;

  const depNms = [dept.depNm1, dept.depNm2, dept.depNm3, dept.depNm4].filter(
    (n) => n && String(n).trim() !== ''
  );
  const depPathKey = depNms.join(' - ');

  const SyncNodeInternals = useCallback(() => {
    const headerEl = headerRef.current;
    if (headerEl) {
      const nextCenter = Math.max(headerEl.offsetHeight / 2, 12);
      setSideHandleCenterY((prev) =>
        Math.abs(prev - nextCenter) > 0.5 ? nextCenter : prev
      );
    }
    updateNodeInternals(id);
  }, [id, updateNodeInternals]);

  useLayoutEffect(() => {
    SyncNodeInternals();
  }, [
    SyncNodeInternals,
    members.length,
    dept.depNmLast,
    depPathKey,
    isDragOver,
    selected,
  ]);

  useLayoutEffect(() => {
    const rootEl = rootRef.current;
    const headerEl = headerRef.current;
    if (!rootEl || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      SyncNodeInternals();
    });
    observer.observe(rootEl);
    if (headerEl) {
      observer.observe(headerEl);
    }
    return () => observer.disconnect();
  }, [SyncNodeInternals]);

  const borderColor = isDragOver || selected ? '#137fec' : '#e2e8f0';
  const borderWidth = isDragOver || selected ? 2 : 1;

  return (
    <Box
      ref={rootRef}
      className="sap-org-dept-node"
      sx={{
        width: ORG_DEPT_NODE_WIDTH,
        minWidth: ORG_DEPT_NODE_WIDTH,
        maxWidth: ORG_DEPT_NODE_WIDTH,
        position: 'relative',
        boxSizing: 'border-box',
        [`& .${HANDLE_CLASS}`]: {
          width: HANDLE_SIZE,
          height: HANDLE_SIZE,
          minWidth: HANDLE_SIZE,
          minHeight: HANDLE_SIZE,
          background: '#94a3b8',
          border: '1.5px solid #fff',
          zIndex: 3,
        },
      }}
    >
      {PORTS.map((p) => (
        <Handle
          key={`s-${p.id}`}
          type="source"
          position={p.position}
          id={p.id}
          className={HANDLE_CLASS}
          style={GetHandleStyle(p.id, sideHandleCenterY)}
        />
      ))}
      {PORTS.map((p) => (
        <Handle
          key={`t-${p.id}`}
          type="target"
          position={p.position}
          id={p.id}
          className={HANDLE_CLASS}
          style={GetHandleStyle(p.id, sideHandleCenterY)}
        />
      ))}
      <Card
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          border: `${borderWidth}px solid ${borderColor}`,
          bgcolor: isDragOver ? 'rgba(19, 127, 236, 0.05)' : '#fff',
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          m: 0,
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          },
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const dataStr = e.dataTransfer?.getData('application/json');
          if (!dataStr || !actions?.onMemberDrop) return;
          try {
            const parsed = JSON.parse(dataStr);
            if (parsed.type === 'member') {
              actions.onMemberDrop(parsed.item, dept.id);
            }
          } catch {
            // —
          }
        }}
        onDragOver={(e) => {
          if (e.dataTransfer?.types?.includes('application/json')) {
            e.preventDefault();
            e.stopPropagation();
            actions?.onMemberDragOver?.(dept.id);
          }
        }}
        onDragLeave={(e) => {
          const related = e.relatedTarget;
          if (!related || !e.currentTarget.contains(related)) {
            actions?.onMemberDragLeave?.();
          }
        }}
      >
        <CardContent
          sx={{
            p: 1,
            '&:last-child': { pb: 1 },
            boxSizing: 'border-box',
          }}
        >
          <Box
            ref={headerRef}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              minHeight: ORG_DEPT_HEADER_MIN_HEIGHT,
              mb: members.length > 0 ? 0.5 : 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  color: '#1e293b',
                  textTransform: 'uppercase',
                  letterSpacing: '-0.02em',
                  wordBreak: 'break-word',
                  lineHeight: 1.35,
                }}
              >
                {dept.depNmLast}
              </Typography>
              {depNms.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.625rem',
                    fontWeight: 500,
                    color: '#64748b',
                    display: 'block',
                    mt: 0.5,
                    wordBreak: 'break-word',
                    lineHeight: 1.35,
                  }}
                >
                  {depPathKey}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              className={`nodrag ${SAP_ORG_EXPORT_HIDE_CLASS}`}
              onClick={(e) => {
                e.stopPropagation();
                actions?.onDepartmentDelete?.(dept.id);
              }}
              sx={{
                p: 0.25,
                flexShrink: 0,
                color: '#ef4444',
                '&:hover': { bgcolor: '#fee2e2' },
              }}
            >
              <DeleteIcon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
          </Box>
          {members.length > 0 && (
            <Box
              className="nowheel"
              sx={{
                mt: 0.5,
                maxHeight: 112,
                overflowY: 'auto',
                pr: 0.25,
              }}
            >
              {members.map((member) => (
                <Box
                  key={member.id}
                  className="nodrag"
                  sx={{
                    p: 0.5,
                    mb: 0.35,
                    bgcolor: '#f1f5f9',
                    borderRadius: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        border: '1px solid #e2e8f0',
                        bgcolor: '#fff',
                        flexShrink: 0,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: '0.85rem' }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#1e293b',
                        }}
                      >
                        {member.NAME || member.name}
                      </Typography>
                      {(member.POSITION || member.position) && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.5625rem',
                            fontWeight: 500,
                            color: '#137fec',
                            textTransform: 'uppercase',
                          }}
                        >
                          {member.POSITION || member.position}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    className={`nodrag ${SAP_ORG_EXPORT_HIDE_CLASS}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      actions?.onMemberRemove?.(member.id);
                    }}
                    sx={{
                      p: 0.25,
                      flexShrink: 0,
                      color: '#ef4444',
                      '&:hover': { bgcolor: '#fee2e2' },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: '0.85rem' }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default memo(SAP_BASIC_01_DeptNode);
